import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Icon, Label, Loader, Message, Popup, Progress, Statistic, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import Button from '../../../../components/Button';
import {
  YelumBatchImport,
  YelumImportItem,
  confirmYelumBatchImport,
  deleteYelumBatchImport,
  downloadYelumBatchImportReport,
  getYelumBatchImport
} from '../../../../services/yelum-batch-imports';
import { dangerNotification, successNotification } from '../../../../services/notification';

const PageContainer = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  h1 {
    margin: 0 0 8px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Card = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const TableContainer = styled(Card)`
  overflow-x: auto;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: 0;
  }
`;

const Muted = styled.span`
  color: #666;
  font-size: 13px;
`;

const activeStatuses = ['PENDING', 'RUNNING', 'PROCESSING'];
const completedStatuses = ['SUCCESS', 'COMPLETED'];

const getProgress = (data: YelumBatchImport): number => {
  if (typeof data.progress === 'number') {
    return Math.min(100, Math.max(0, data.progress <= 1 ? data.progress * 100 : data.progress));
  }
  if (data.totalItems && typeof data.processedItems === 'number') {
    return Math.min(100, (data.processedItems / data.totalItems) * 100);
  }
  return completedStatuses.includes(data.status) ||
    data.status === 'COMPLETED_WITH_ERRORS'
    ? 100
    : 0;
};

const getItemValue = (item: YelumImportItem, keys: string[]): string => {
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== '') return String(value);
  }
  return '-';
};

const getMatchStatus = (item: YelumImportItem): string => {
  return String(item.matchStatus || item.matchType || item.status || '').toUpperCase();
};

const isMatched = (item: YelumImportItem): boolean => {
  const status = getMatchStatus(item);
  return ['MATCHED', 'FOUND', 'SUCCESS', 'MATCH'].includes(status) ||
    Boolean(item.matchedPolicy || item.matchedPolicyId || item.matchResults?.length);
};

const isMissing = (item: YelumImportItem): boolean => {
  const status = getMatchStatus(item);
  return ['NOT_FOUND', 'MISSING', 'NOT_MATCHED', 'ABSENT'].includes(status);
};

const getStatusText = (status?: string) => {
  const labels: Record<string, string> = {
    DRAFT: 'Revisão pendente',
    PENDING: 'Pendente',
    RUNNING: 'Processando',
    PROCESSING: 'Processando',
    SUCCESS: 'Concluída',
    COMPLETED: 'Concluída',
    COMPLETED_WITH_ERRORS: 'Concluída com erros',
    FAILED: 'Erro',
    CANCELLED: 'Descartada'
  };
  return labels[status || ''] || status || 'Desconhecido';
};

const YelumImportDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [importData, setImportData] = useState<YelumBatchImport | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [cancelMatchedPolicies, setCancelMatchedPolicies] = useState(false);
  const [processInsurerCancellation, setProcessInsurerCancellation] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const loadImport = useCallback(
    async (showLoader = false) => {
      if (showLoader) setLoading(true);
      try {
        const data = await getYelumBatchImport(id);
        setImportData(data);
        if (
          data.cancelMatchedPolicies !== undefined ||
          data.processInsurerCancellation !== undefined ||
          data.cancelMissingItems !== undefined
        ) {
          setCancelMatchedPolicies(
            Boolean(data.cancelMatchedPolicies ?? data.cancelMissingItems)
          );
          setProcessInsurerCancellation(
            Boolean(data.processInsurerCancellation ?? data.cancelMissingItems)
          );
        }
      } catch (error) {
        dangerNotification(
          'Erro',
          (error as Error)?.message || 'Não foi possível carregar a importação.'
        );
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadImport(true);
  }, [loadImport]);

  useEffect(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (importData && activeStatuses.includes(importData.status)) {
      intervalRef.current = window.setInterval(() => loadImport(), 10000);
    }
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [importData, loadImport]);

  const items = useMemo(
    () => importData?.items || importData?.results || [],
    [importData]
  );
  const matchedItems = items.filter(isMatched);
  const batchWarnings = importData ? importData.warnings : undefined;
  const warningMessages = importData ? importData.warningMessages : undefined;
  const warnings: string[] = Array.isArray(batchWarnings)
    ? batchWarnings
    : Array.isArray(warningMessages)
      ? warningMessages.map(String)
      : [];
  const showInsurerCancellationToggle =
    matchedItems.length > 0 ||
    Number(importData?.matchedItems || importData?.matchedCount || 0) > 0;
  const missingOwnerCount = items.filter(
    item => item.missingOwner || (!item.propertyOwnerName && !item.propertyOwnerDocument)
  ).length;
  const progress = importData ? getProgress(importData) : 0;

  const handleDiscard = async () => {
    if (!window.confirm('Deseja descartar esta importação?')) return;
    setActionLoading(true);
    try {
      await deleteYelumBatchImport(id);
      successNotification('Sucesso', 'Importação descartada.');
      history.push('/admin/fire-insurance-batches/yelum-imports');
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Não foi possível descartar a importação.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      await confirmYelumBatchImport(id, {
        cancelMatchedPolicies,
        processInsurerCancellation: cancelMatchedPolicies && processInsurerCancellation
      });
      successNotification('Importação confirmada', 'O processamento foi iniciado.');
      await loadImport(true);
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Não foi possível confirmar a importação.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadYelumBatchImportReport(id);
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Não foi possível baixar o relatório.'
      );
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !importData) {
    return (
      <PageContainer>
        <Loader active inline="centered" size="large">
          Carregando importação...
        </Loader>
      </PageContainer>
    );
  }

  const isDraft = importData.status === 'DRAFT';
  const isPolling = activeStatuses.includes(importData.status);
  const canDownload =
    completedStatuses.includes(importData.status) ||
    importData.status === 'COMPLETED_WITH_ERRORS' ||
    importData.status === 'FAILED';

  return (
    <PageContainer>
      <Header>
        <div>
          <h1>
            <Icon name="file alternate outline" /> Revisão da importação Yelum
          </h1>
          <Muted>
            {importData.fileName || importData.filename || importData.originalFileName || '-'}
            {' · '}
            {importData.estate?.name || importData.estateId || 'Imobiliária não informada'}
          </Muted>
        </div>
        <HeaderActions>
          <Button basic onClick={() => history.push('/admin/fire-insurance-batches/yelum-imports')}>
            <Icon name="arrow left" /> Histórico
          </Button>
          {canDownload && (
            <Button basic color="green" loading={downloading} onClick={handleDownload}>
              <Icon name="download" /> Baixar relatório
            </Button>
          )}
        </HeaderActions>
      </Header>

      {!isDraft && (
        <Card>
          <h3>
            Status: <Label color={isPolling ? 'yellow' : completedStatuses.includes(importData.status) ? 'green' : 'red'}>
              {getStatusText(importData.status)}
            </Label>
          </h3>
          <Progress
            percent={progress}
            color={isPolling ? 'yellow' : completedStatuses.includes(importData.status) ? 'green' : 'red'}
            active={isPolling}
          >
            {Math.round(progress)}%
          </Progress>
          <Statistic.Group widths="four" size="mini">
            <Statistic>
              <Statistic.Value>{importData.totalItems ?? items.length}</Statistic.Value>
              <Statistic.Label>Total de itens</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{importData.processedItems ?? importData.successItems ?? 0}</Statistic.Value>
              <Statistic.Label>Processados</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{importData.matchedItems ?? matchedItems.length}</Statistic.Value>
              <Statistic.Label>Encontrados</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{importData.failedItems ?? 0}</Statistic.Value>
              <Statistic.Label>Com erro</Statistic.Label>
            </Statistic>
          </Statistic.Group>
          {importData.errorMessage && <Message negative>{importData.errorMessage}</Message>}
        </Card>
      )}

      {isDraft && warnings.length > 0 && (
        <Message warning>
          <Message.Header>Atenção antes de confirmar</Message.Header>
          <ul>
            {warnings.map((warning, index) => <li key={index}>{String(warning)}</li>)}
          </ul>
        </Message>
      )}
      {isDraft && missingOwnerCount > 0 && (
        <Message warning>
          {missingOwnerCount} item(ns) não possuem dados do proprietário na planilha.
          A importação continuará e esses dados ficarão pendentes para complementação.
        </Message>
      )}

      {isDraft && (
        <Card>
          <h3>Comportamentos da confirmação</h3>
          {showInsurerCancellationToggle && (
            <ToggleRow>
              <Checkbox
                toggle
                checked={cancelMatchedPolicies}
                onChange={(_, data) => {
                  const checked = Boolean(data.checked);
                  setCancelMatchedPolicies(checked);
                  if (!checked) setProcessInsurerCancellation(false);
                }}
              />
              <div>
                <strong>Deseja cancelar as apólices encontradas em outras seguradoras?</strong>
                <Popup
                  trigger={<Icon name="info circle" color="grey" style={{ marginLeft: 6 }} />}
                  content="Se ativado, as apólices ativas que deram match serão marcadas como canceladas no nosso sistema."
                  position="top center"
                />
                <div><Muted>Desative para manter as apólices correspondentes sem cancelamento.</Muted></div>
              </div>
            </ToggleRow>
          )}
          {cancelMatchedPolicies && (
            <ToggleRow>
              <Checkbox
                toggle
                checked={processInsurerCancellation}
                onChange={(_, data) => setProcessInsurerCancellation(Boolean(data.checked))}
              />
              <div>
                <strong>Processar também os cancelamentos na seguradora?</strong>
                <Popup
                  trigger={<Icon name="info circle" color="grey" style={{ marginLeft: 6 }} />}
                  content="Se ativado, nossa API fará requisições para a seguradora tentando realizar os cancelamentos dos itens. Se desativado, nosso sistema apenas os marcará como cancelados; cabe à operação verificar o cancelamento real na seguradora."
                  position="top center"
                />
                <div><Muted>Quando desativado, apenas o cancelamento local será realizado.</Muted></div>
              </div>
            </ToggleRow>
          )}
          {!showInsurerCancellationToggle && (
            <Muted>Nenhuma ação adicional foi identificada para esta importação.</Muted>
          )}
        </Card>
      )}

      <TableContainer>
        <h3>Itens e correspondências ({items.length})</h3>
        <Table celled striped compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Item</Table.HeaderCell>
              <Table.HeaderCell>Apólice</Table.HeaderCell>
              <Table.HeaderCell>Inquilino</Table.HeaderCell>
              <Table.HeaderCell>Proprietário</Table.HeaderCell>
              <Table.HeaderCell>Correspondência</Table.HeaderCell>
              <Table.HeaderCell>Status / mensagem</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.length === 0 ? (
              <Table.Row>
              <Table.Cell colSpan={6} textAlign="center">Nenhum item encontrado.</Table.Cell>
              </Table.Row>
            ) : (
              items.map((item, index) => {
                const match = isMatched(item);
                const missing = isMissing(item);
                return (
                  <Table.Row key={item.id || `${getItemValue(item, ['itemNo', 'item'])}-${index}`}>
                    <Table.Cell>{getItemValue(item, ['itemNo', 'itemNumber', 'item'])}</Table.Cell>
                    <Table.Cell>{getItemValue(item, ['policyNumber', 'policyNo', 'policy'])}</Table.Cell>
                    <Table.Cell>{getItemValue(item, ['tenantName', 'name'])}</Table.Cell>
                    <Table.Cell>
                      {item.missingOwner || (!item.propertyOwnerName && !item.propertyOwnerDocument)
                        ? 'Não informado'
                        : getItemValue(item, ['propertyOwnerName'])}
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={match ? 'green' : missing ? 'orange' : 'grey'}>
                        {match ? 'Encontrado' : missing ? 'Não encontrado' : getMatchStatus(item) || 'Pendente'}
                      </Label>
                      {(item.matchedPolicy || item.matchedPolicyId || item.matchResults?.length) && (
                        <div>
                          <Muted>
                            {item.matchedPolicyId ||
                              (item.matchedPolicy &&
                                getItemValue(item.matchedPolicy as YelumImportItem, [
                                  'policyNumber',
                                  'externalPolicyCode',
                                  'id'
                                ])) ||
                              `${item.matchResults.length} correspondência(s)`}
                          </Muted>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {item.errorMessage ||
                        item.message ||
                        item.matchReason ||
                        getItemValue(item, ['result', 'processingStatus', 'status'])}
                    </Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </TableContainer>

      {isDraft && (
        <HeaderActions>
          <Button basic color="red" loading={actionLoading} disabled={actionLoading} onClick={handleDiscard}>
            <Icon name="trash" /> Descartar
          </Button>
          <Button color="purple" loading={actionLoading} disabled={actionLoading} onClick={handleConfirm}>
            <Icon name="checkmark" /> Confirmar e processar
          </Button>
        </HeaderActions>
      )}
    </PageContainer>
  );
};

export default YelumImportDetailsPage;
